import React, { useState, useEffect, useRef } from 'react';
import _, { remove, set } from 'lodash';
import { OwnerInterface } from './types';
import useApi from './api';
import { useOutsideAlerter } from './useOutsideAlerter';


const ConsumerSearch = ({ onSearch }: { onSearch: any }) => {
    const [input, setInput] = useState('');
    const { get, getStream, post, postStream, put, patch, remove } = useApi();

    // Debounced search function
    const debouncedSearch = useRef(_.debounce(onSearch, 300)).current;

    useEffect(() => {
        if (input) {
            debouncedSearch(input);
        }
    }, [input, debouncedSearch]);

    return (

        <div className="">
            <div className="w-1/2 w-full">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                    <input
                        type="text"
                        id="search"
                        autoComplete="off"
                        className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-gray-900 placeholder:text-gray-400 rounded-md focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-sm focus:ring-opacity-50 sm:leading-6"
                        placeholder="Select viewers..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                    />
                </div>
            </div>
        </div>
    );
};

const Consumer = ({ addConsumer, removeConsumer, consumers, color }: { addConsumer: any, removeConsumer: any, consumers: OwnerInterface[],  color?: string }) => {
    const [searchResults, setSearchResults] = useState<OwnerInterface[]>([]);
    const [currentConsumers, setCurrentConsumers] = useState<OwnerInterface[]>(consumers);
    const [modalVisible, setModalVisible] = useState(false); // State to manage modal visibility
    const { get, getStream, post, postStream, put, patch, remove } = useApi();

    const filterSearchResults = (results: any) => {
        // filter out any of the current consumers
        return results.filter((result: any) => !currentConsumers.some((consumer) => consumer.id === result.id));
    };

    const handleSearch = async (search: string) => {
        try {
            const response = await get(`/owners/?search=${search}`);
            setSearchResults(filterSearchResults(response.data));
        } catch (error) {
            console.error('Error searching users:', error);
        }

    };

    const toggleModal = () => {
        setModalVisible(!modalVisible);
        setSearchResults([]);
    };

    const selectUser = (owner: OwnerInterface) => {
        setCurrentConsumers((prevConsumers) => [...prevConsumers, owner]);
        setSearchResults([]);
        addConsumer(owner);
        setModalVisible(false);
    };

    const handleRemove = (consumer: OwnerInterface) => {
        setCurrentConsumers(currentConsumers.filter((user) => user.id !== consumer.id));
        removeConsumer(consumer);
    };

    const modalRef = useRef(null);

    useOutsideAlerter(modalRef, () => toggleModal());

    const iconColor = color ? color : 'blue';

    return (
        <div className='mt-4'>
            <label className="block text-sm font-medium leading-6 text-gray-900">
                Viewers
            </label>
            <div onClick={toggleModal} className="flex flex-row items-center mt-2 p-2 pl-2 border rounded-full hover:shadow-md hover:cursor-pointer border-dashed border-gray-400 w-11">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="" className="w-6 h-6 stroke-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                    </svg>
                    </div>
            {currentConsumers && currentConsumers.length > 0 && (
                          <div className="flex flex-col mt-4">
                          <div className="-m-1.5">
                            <div className="p-1.5 min-w-full inline-block align-middle">
                              <div className="border rounded-lg shadow overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase">
                                        Team
                                      </th>
                                      <th scope="col" className="px-6 py-3 text-end text-xs font-medium text-gray-500 uppercase">
                                
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {currentConsumers.length > 0 &&
                                      currentConsumers.map((consumer: OwnerInterface) => (
                                        <tr key={consumer.id}>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                                            {consumer.name}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                                            <button onClick={() => handleRemove(consumer)} type="button" className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:pointer-events-none">Remove</button>
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
            )}

            {modalVisible && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50" id="my-modal">
                    <div ref={modalRef} className="relative top-60 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <ConsumerSearch onSearch={handleSearch} />
                        {searchResults.length > 0 && (
                <div className="border w-full bg-gray-50 shadow-md rounded-b-md">
                    {searchResults.map((owner) => (
                        <div key={owner.id}
                            className="p-2 hover:bg-gray-100 text-sm"
                            onClick={() => selectUser(owner)}>
                            <div className="flex flex-row items-center">
                                <div className={`w-8 h-8 mr-2 flex items-center justify-center rounded-full bg-${iconColor}-500 text-white`}>
                                    <span className="text-md font-semibold">{owner.name[0]}</span>
                                </div>
                                <div>
                                    <div>
                                        {/* {owner.first_name} {owner.last_name} */}
                                    </div>
                                    <div className="text-gray-400">
                                        {owner.name}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Consumer;